// Estado de la bandeja de despliegues en el navegador: se pide al abrir la oficina, cada 30 s y al volver a
// la pestaña. El servidor ya cachea 20 s lo que pide a GitHub, así que varias pestañas no gastan más cuota.
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { desplieguesApi, type Bandeja, type Despliegue, type EstadoMasInfo, type MasInfo, type MensajeHilo } from './api';
import { subidasPorAgente } from './subidas';

interface EstadoBandeja {
  bandeja: Bandeja | null;
  /**
   * 04-11 (Entrega 3) — la subida abierta en su ventana (runId); null = ninguna. Se guarda solo QUÉ subida: la subida en
   * sí sale siempre de la bandeja viva (29-09). La abren la sección suelta, la fila de su agente («Revisar»), su chat
   * («Revisar la subida») y Ctrl+K; la ventana es una sola (SubidasVivas, en la página).
   */
  abierta: number | null;
  abrirSubida: (runId: number | null) => void;
  error: boolean;
  /** Maqueta: datos fijos y sin pedir nada al servidor. */
  simulada: boolean;
  cargar: (fresco?: boolean) => Promise<void>;
  simular: (b: Bandeja | null, extras?: { masInfo?: Record<number, MasInfo>; hilos?: Record<string, MensajeHilo[]> }) => void;
  /** Brief §10: la explicación larga y la conversación con quien lo pidió, por despliegue. */
  masInfo: Record<number, EstadoMasInfo>;
  /** Por `claveHilo`: la conversación de la subida o la de una línea («Preguntar», 13-10). */
  hilos: Record<string, MensajeHilo[]>;
  cargarMasInfo: (runId: number) => Promise<void>;
  cargarHilo: (runId: number, linea?: string) => Promise<void>;
  escribir: (runId: number, texto: string, linea?: string) => Promise<void>;
}

/** La conversación de una subida (sin línea) o la de una de sus líneas: cada una con el agente que la hizo. */
export const claveHilo = (runId: number, linea?: string): string => (linea ? `${runId}:${linea}` : String(runId));

export const useBandejaStore = create<EstadoBandeja>((set, get) => ({
  bandeja: null,
  abierta: null,
  abrirSubida: (runId) => set({ abierta: runId }),
  error: false,
  simulada: false,
  cargar: async (fresco = false) => {
    if (get().simulada) return;
    try {
      const bandeja = await desplieguesApi.bandeja(fresco);
      // Una respuesta que llega tarde no pisa unos datos simulados puestos mientras tanto (maqueta).
      if (!get().simulada) set({ bandeja, error: false });
    } catch {
      if (!get().simulada) set({ error: true });
    }
  },
  simular: (b, extras) =>
    set({
      bandeja: b,
      simulada: b !== null,
      error: false,
      masInfo: Object.fromEntries(Object.entries(extras?.masInfo ?? {}).map(([k, info]) => [k, { estado: 'listo' as const, info }])),
      hilos: extras?.hilos ?? {},
    }),
  masInfo: {},
  hilos: {},
  cargarMasInfo: async (runId) => {
    if (get().simulada) return;
    const r = await desplieguesApi.masInfo(runId);
    set((s) => ({ masInfo: { ...s.masInfo, [runId]: r } }));
  },
  cargarHilo: async (runId, linea) => {
    if (get().simulada) return;
    const r = await desplieguesApi.hilo(runId, linea);
    set((s) => ({ hilos: { ...s.hilos, [claveHilo(runId, linea)]: r.mensajes } }));
  },
  escribir: async (runId, texto, linea) => {
    const k = claveHilo(runId, linea);
    if (!get().simulada) {
      const r = await desplieguesApi.escribir(runId, texto, linea);
      set((s) => ({ hilos: { ...s.hilos, [k]: r.mensajes } }));
      return;
    }
    // Maqueta: el mensaje entra y, al rato, el agente contesta con una respuesta fija.
    const ahora = new Date().toISOString();
    set((s) => ({ hilos: { ...s.hilos, [k]: [...(s.hilos[k] ?? []), { id: `p-${Date.now()}`, de: 'pau', texto, ts: ahora }] } }));
    setTimeout(() => {
      set((s) => ({
        hilos: {
          ...s.hilos,
          [k]: [...(s.hilos[k] ?? []), { id: `a-${Date.now()}`, de: 'agente', texto: 'Buena pregunta. Te lo miro con datos y te contesto aquí mismo en un momento.', ts: new Date().toISOString() }],
        },
      }));
    }, 1400);
  },
}));

const CADA_MS = 30_000;

/** Mantiene la bandeja al día mientras el componente que la usa está en pantalla. */
export function useBandejaViva(): void {
  const cargar = useBandejaStore((s) => s.cargar);
  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), CADA_MS);
    const alVolver = () => document.visibilityState === 'visible' && void cargar();
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [cargar]);
}

const SIN_PENDIENTES: Despliegue[] = [];

/** Las subidas pendientes y, por agente, la suya (subidas.ts). No pide nada: la mantiene viva la página (SubidasVivas). */
export function useSubidas(): { pendientes: Despliegue[]; porAgente: ReadonlyMap<string, Despliegue> } {
  const pendientes = useBandejaStore((s) => s.bandeja?.pendientes ?? SIN_PENDIENTES);
  const porAgente = useMemo(() => subidasPorAgente(pendientes), [pendientes]);
  return { pendientes, porAgente };
}
